import {
  AssetPermission,
  OrganizationPermission,
  Role,
} from '@gm-commercial/profile-model';
import {
  CATALOG_FLAG,
  FLEETIO_ACCOUNT_LINKING_FLAG,
  FUEL_CARD_AND_EXPENSES_FLAG,
  SHOP_ADD_ONS_FLAG,
} from '~/common/models/featureFlags.model';

import { RouteName } from '~/common/models/route.model';
import { lazyWithReload } from '~/common/utils/lazyWithReload';
import CatalogTemplate from '~/layouts/CatalogTemplate';

const Catalog = lazyWithReload(
  () => import('~/features/catalog/catalog/Catalog')
);
const PlanOrderSummary = lazyWithReload(
  () => import('~/features/catalog/checkout/PlanOrderSummary')
);
const DeviceOrderSummary = lazyWithReload(
  () => import('~/features/catalog/checkout/addons/DeviceOrderSummary')
);
const DeviceOrderConfirmation = lazyWithReload(
  () =>
    import(
      '~/features/catalog/checkout/addons/components/deviceOrderConfirmation/DeviceOrderConfirmation'
    )
);
const OrderConfirmation = lazyWithReload(
  () => import('~/features/catalog/orders/OrderConfirmation')
);
const ShopAddons = lazyWithReload(
  () => import('~/features/catalog/addons/Addons')
);
const ShopIntegrations = lazyWithReload(
  () => import('~/features/integrations/shop/IntegrationsPage')
);
const ShopRootRedirect = lazyWithReload(() => import('./ShopRootRedirect'));

const ORGANIZATIONS_SHOP_ROUTES = {
  [RouteName.SHOP]: {
    name: RouteName.SHOP,
    path: '/organizations/:organizationsId/shop',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: ShopRootRedirect,
    requiredFeatureFlags: [CATALOG_FLAG],
    customTemplate: CatalogTemplate,
  },
  [RouteName.SHOP_PLANS]: {
    name: RouteName.SHOP_PLANS,
    path: '/organizations/:organizationsId/shop/plans',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: Catalog,
    requiredFeatureFlags: [CATALOG_FLAG],
    customTemplate: CatalogTemplate,
  },
  [RouteName.PLAN_ORDER_SUMMARY]: {
    name: RouteName.PLAN_ORDER_SUMMARY,
    path: '/organizations/:organizationsId/shop/order',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: PlanOrderSummary,
  },
  [RouteName.ORDER_CONFIRMATION]: {
    name: RouteName.ORDER_CONFIRMATION,
    path: '/organizations/:organizationsId/shop/order/confirmation',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: OrderConfirmation,
  },
  [RouteName.SHOP_ADDONS]: {
    name: RouteName.SHOP_ADDONS,
    path: '/organizations/:organizationsId/shop/add-ons',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    requiredFeatureFlags: [SHOP_ADD_ONS_FLAG],
    element: ShopAddons,
    customTemplate: CatalogTemplate,
  },
  [RouteName.SHOP_ADDON_ORDER]: {
    name: RouteName.SHOP_ADDON_ORDER,
    path: '/organizations/:organizationsId/shop/add-ons/order',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    requiredFeatureFlags: [SHOP_ADD_ONS_FLAG],
    element: DeviceOrderSummary,
  },
  [RouteName.SHOP_ADDON_ORDER_CONFIRMATION]: {
    name: RouteName.SHOP_ADDON_ORDER_CONFIRMATION,
    path: '/organizations/:organizationsId/shop/add-ons/order/confirmation',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    requiredFeatureFlags: [SHOP_ADD_ONS_FLAG],
    element: DeviceOrderConfirmation,
  },
  [RouteName.ORGANIZATIONS_SHOP_INTEGRATIONS]: {
    name: RouteName.ORGANIZATIONS_SHOP_INTEGRATIONS,
    path: '/organizations/:organizationsId/shop/integrations',
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    // Visible if either partner's flag is on (see route.model.ts anyFeatureFlags).
    anyFeatureFlags: [
      FLEETIO_ACCOUNT_LINKING_FLAG,
      FUEL_CARD_AND_EXPENSES_FLAG,
    ],
    element: ShopIntegrations,
    customTemplate: CatalogTemplate,
  },
};

export default ORGANIZATIONS_SHOP_ROUTES;
